import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'

import { Select, Space } from '@procurify/ui'
import { DateTime, Interval } from 'luxon'

import { type TimezonesEnum } from '@webapp/core/entities/external/enums/TimezonesEnum'
import { useProcurifyIntl } from '@webapp/hooks'
import {
  useFormatBudget,
  useCalculateBudgetSubtotals,
} from '@webapp/hooks/useFormatBudget'
import { useFormatBudgetPeriod } from '@webapp/hooks/useFormatBudgetPeriod'
import { useFormatISOStringToDate } from '@webapp/hooks/useFormatISOStringToDate'
import {
  type BudgetCategoryDetails,
  type BudgetGroupBudget,
} from '@webapp/models'
import messages from './messages'
import { BudgetCategoryCard } from '../BudgetCategoryCard'
import { BudgetCategoryDetailsCardStyled } from './styles/BudgetCategoryDetailsCardStyled'

export interface IBudgetCategoryDetailsCardProps {
  budgetCategory: BudgetCategoryDetails
  headquartersTimezone: TimezonesEnum
  onEmptyStateButtonClick: () => void
  renderMenuButton?: () => ReactNode
}

export const BudgetCategoryDetailsCard = ({
  budgetCategory,
  headquartersTimezone,
  onEmptyStateButtonClick,
  renderMenuButton = () => undefined,
}: IBudgetCategoryDetailsCardProps) => {
  const intl = useProcurifyIntl()
  const todayInDateTime = DateTime.now()

  const [selectedBudgetPeriod, setSelectedBudgetPeriod] = useState(null)
  const [budgetPeriodOptions, setBudgetPeriodOptions] = useState(null)
  const [currentBudgetGroup, setCurrentBudgetGroup] = useState(null)

  const formatISOStringToDate = useFormatISOStringToDate()
  const formatBudgetPeriod = useFormatBudgetPeriod()

  const { budget: currentBudget, setBudgetToFormat } = useFormatBudget()
  const [selectedBudget, setSelectedBudget] = useState<BudgetGroupBudget>()
  const calculateBudgetSubtotals = useCalculateBudgetSubtotals()
  const subtotals = calculateBudgetSubtotals(selectedBudget)

  const setBudget = (budget: BudgetGroupBudget | null) => {
    setSelectedBudget(budget)
    setBudgetToFormat(budget)
  }

  useEffect(() => {
    const { budget_groups } = budgetCategory

    const currBudgetGroup = budget_groups.find((group) => {
      const { start_datetime, end_datetime } = group

      const startDate = formatISOStringToDate(
        start_datetime,
        headquartersTimezone
      )
      const endDate = formatISOStringToDate(end_datetime, headquartersTimezone)
      const budgetGroupDateTimeInterval = Interval.fromDateTimes(
        startDate,
        endDate
      )

      if (budgetGroupDateTimeInterval.contains(todayInDateTime)) {
        setCurrentBudgetGroup(group)
        return group
      }
    })

    const { budgets: budgets = [], interval } = currBudgetGroup || {}

    const budgetOptions = []

    if (interval !== null) {
      budgets
        .sort((a, b) => {
          const startDateA = DateTime.fromISO(a.start_datetime, {
            zone: headquartersTimezone,
          }).toMillis()

          const startDateB = DateTime.fromISO(b.start_datetime, {
            zone: headquartersTimezone,
          }).toMillis()

          return startDateA - startDateB
        })
        .map((budget) => {
          const { start_datetime, end_datetime, id } = budget

          const startDate = formatISOStringToDate(
            start_datetime,
            headquartersTimezone
          )
          const endDate = formatISOStringToDate(
            end_datetime,
            headquartersTimezone
          )

          const budgetDateTimeInterval = Interval.fromDateTimes(
            startDate,
            endDate
          )

          let label = formatBudgetPeriod(
            start_datetime,
            end_datetime,
            interval,
            headquartersTimezone
          )

          if (budgetDateTimeInterval.contains(todayInDateTime)) {
            label = label.concat(
              `${intl.formatMessage(messages.currentBudgetPeriodOptionSuffix)}`
            )
            setSelectedBudgetPeriod({
              label,
              value: id,
            })
            setBudget(budget)
          }

          budgetOptions.push({
            label,
            value: id,
          })
        })

      setBudgetPeriodOptions(budgetOptions)
    } else {
      budgets?.find((budget) => {
        const { start_datetime, end_datetime } = budget

        const startDate = formatISOStringToDate(
          start_datetime,
          headquartersTimezone
        )
        const endDate = formatISOStringToDate(
          end_datetime,
          headquartersTimezone
        )

        const budgetDateTimeInterval = Interval.fromDateTimes(
          startDate,
          endDate
        )

        if (budgetDateTimeInterval.contains(todayInDateTime)) setBudget(budget)
      })
    }

    return () => {
      setBudget(null)
      setSelectedBudgetPeriod(null)
      setBudgetPeriodOptions(null)
      setCurrentBudgetGroup(null)
    }
  }, [budgetCategory])

  const handleOnBudgetPeriodSelect = (option) => {
    setSelectedBudgetPeriod(option)

    const { budgets } = currentBudgetGroup

    const currBudget = budgets
      ? budgets.find((budget) => {
          const { id } = budget

          if (id === option.value) return budget
        })
      : null

    setBudget(currBudget)
  }

  return (
    <BudgetCategoryDetailsCardStyled>
      <BudgetCategoryCard>
        <Space size='medium'>
          <BudgetCategoryCard.Header
            name={budgetCategory.name}
            startDate={currentBudget?.startDateTime}
            endDate={currentBudget?.endDateTime}
            renderMenuButton={renderMenuButton}
            budgetSelect={
              budgetPeriodOptions?.length ? (
                <Space.Item width={1 / 2}>
                  <Select
                    value={selectedBudgetPeriod}
                    options={budgetPeriodOptions}
                    getOptionValue={(option) => option.value}
                    onChange={handleOnBudgetPeriodSelect}
                  />
                </Space.Item>
              ) : null
            }
          />
          {selectedBudget ? (
            <>
              {subtotals.budgetType === 'fixed' && (
                <BudgetCategoryCard.ProgressBar
                  totalSpend={subtotals.spendTotal}
                  committedSpend={subtotals.committed}
                  spendRequest={subtotals.spendRequest}
                  pendingRequests={subtotals.pending}
                  remainingAfterPendingRequests={
                    subtotals.remainingMinusPending
                  }
                  isOverBudget={subtotals.isOverBudget}
                  largeTooltip
                />
              )}
              <BudgetCategoryCard.Subtotals
                totalBudget={subtotals.total}
                totalSpend={subtotals.spendTotal}
                committedSpend={subtotals.committed}
                approvedSpend={subtotals.approved}
                purchasedSpend={subtotals.purchased}
                billedSpend={subtotals.billed}
                receivedSpend={subtotals.received}
                spendRequest={subtotals.spendRequest}
                remaining={subtotals.remaining}
                pendingRequests={subtotals.pending}
                remainingAfterPendingRequests={subtotals.remainingMinusPending}
                isOverBudget={subtotals.isOverBudget}
                shouldRenderAccordion
                shouldRenderPercentagesFirst={
                  subtotals.budgetType === 'flexible'
                }
              />
            </>
          ) : (
            <BudgetCategoryCard.EmptyState
              onClickEditCategoryButton={onEmptyStateButtonClick}
            />
          )}
        </Space>
      </BudgetCategoryCard>
    </BudgetCategoryDetailsCardStyled>
  )
}

BudgetCategoryDetailsCard.displayName = 'BudgetCategoryDetailsCard'
